import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  useNavigate,
  useLocation,
  NavigateFunction,
  Location,
} from 'react-router-dom'

import { t } from '@/i18n'
import { AppLayout, Button, FormError, LanguageSelector } from '@/components'
import { useApp, useToast } from '@/hooks'
import { homePath } from '@/utils/index'

const SignInPage: React.FC = () => {
  const addToast = useToast()
  const navigate: NavigateFunction = useNavigate()
  const { state: rootState }: Location = useLocation()
  const { onSignIn } = useApp()

  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [error, setError] = useState<Record<'username' | 'password', string>>({
    username: '',
    password: '',
  })
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (username) {
      setError(e => ({ ...e, username: '' }))
    }
  }, [username])

  useEffect(() => {
    if (password) {
      setError(e => ({ ...e, password: '' }))
    }
  }, [password])

  const lastPath: string = useMemo(
    // @ts-ignore
    () => rootState?.from?.pathname || homePath,
    // @ts-ignore
    [rootState?.from?.pathname],
  )

  const validate = useCallback(() => {
    const _error: Record<'username' | 'password', string> = {
      username: '',
      password: '',
    }
    if (!username) {
      _error.username = t('field_required')
    }
    if (!password) {
      _error.password = t('field_required')
    }

    setError(_error)

    return !(Boolean(_error.username) || Boolean(_error.password))
  }, [password, username])

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (validate()) {
        setLoading(true)
        onSignIn({ username, password }, (success: boolean) => {
          if (success) {
            navigate(lastPath || homePath, { replace: true })
          } else {
            addToast('error', t('creds_invalid'))
          }
          setLoading(false)
        })
      }
    },
    [validate, onSignIn, username, password, addToast, navigate, lastPath],
  )

  return (
    <AppLayout headerRight={<LanguageSelector />}>
      <div className="data-wrap centered">
        <div className="content-box">
          <div className="login-form">
            <h1>{t('login')}</h1>
            <form>
              <div className="form-fields">
                <label
                  className={classNames('w-100', {
                    error: !!error.username,
                  })}
                >
                  <input
                    type="text"
                    placeholder={t('user_name')}
                    value={username}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      setUsername(e.currentTarget.value)
                    }
                  />
                  <FormError error={error.username} />
                </label>
                <label
                  className={classNames('w-100', {
                    error: !!error.password,
                  })}
                >
                  <input
                    type="password"
                    placeholder={t('password')}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      setPassword(e.currentTarget.value)
                    }
                  />
                  <FormError error={error.password} />
                </label>
              </div>
              <div className="submit-btn">
                <Button
                  type="submit"
                  disabled={loading}
                  className="main-btn"
                  onClick={onSubmit}
                >
                  {t('login')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default SignInPage
